import {
  Operation,
  StoreName,
  UpdateToOnlineStatus,
} from "constants/serviceWorker";
import {
  DateAdditionalInfo,
  DEFAULT_DATE_FROM_PHOTO_ID,
} from "interfaces/models/inspectionItemResult";
import networkMonitor from "libs/network-monitor/NetworkMonitor";
import { uuid } from "utils/common";
import { addIndexedDBItem, getIndexedDb } from "utils/indexedDb";
import { OnChangeType } from "../fields/types";
import {
  DataType,
  DATA_OFFLINE_UPDATE_TRIGGER_DATA_TYPES,
  DATE_UPDATE_TRIGGER_DATA_TYPES,
} from "../setup/define";
import { AfterChangePinContent } from "../type";

export const changeDate = async (
  params: AfterChangePinContent,
  options?: {
    action: OnChangeType;
  }
) => {
  const {
    dataType,
    itemResultId,
    itemResults,
    data,
    formValue,
    saveFieldValue,
  } = params;

  if (!DATE_UPDATE_TRIGGER_DATA_TYPES.includes(dataType)) return;

  if (options?.action !== OnChangeType.ADD_NEW_IMAGE) return;

  const fieldsValid = itemResults.filter((item) => {
    const additionInfo = item.additionInfo as DateAdditionalInfo;

    return (
      item.id !== itemResultId &&
      item.dataType === DataType.DATE &&
      additionInfo.dateFromPhotoId !== DEFAULT_DATE_FROM_PHOTO_ID &&
      additionInfo?.dateFromPhotoId === itemResultId &&
      !formValue?.[item.id]
    );
  });

  if (!fieldsValid?.length) return;

  return await Promise.all(
    fieldsValid.map((field) =>
      saveFieldValue(
        field.id,
        data.lastModifiedDateFile ?? new Date(),
        field.dataType
      )
    )
  );
};

export const changeDataOffline = async (
  params: AfterChangePinContent,
  options?: {
    action: OnChangeType;
  }
) => {
  const { dataType, itemResultId, pinId, inspectionTypeId, data } = params;
  const action = options?.action;
  if (!DATA_OFFLINE_UPDATE_TRIGGER_DATA_TYPES.includes(dataType)) return;
  const isOnline = networkMonitor.isOnline();
  if (isOnline) {
    return;
  }

  const isAddNewImage = action === OnChangeType.ADD_NEW_IMAGE;

  switch (action) {
    case OnChangeType.DELETE_IMAGE: {
      const id = uuid();
      await addIndexedDBItem({
        id,
        data: {
          id,
          requestTime: Date.now(),
          status: UpdateToOnlineStatus.Success,
          store: StoreName.INSPECTION_PIN_CONTENT,
          isPushed: {},
          operation: Operation.Patch,
          data: {
            itemResultId,
            value: { data: data ?? "" },
            pinId,
            inspectionTypeId,
            dataType,
          },
        },
      });

      break;
    }

    case OnChangeType.EDIT_IMAGE:
    case OnChangeType.ADD_NEW_IMAGE: {
      const id = data?.id ?? data?.offlineId;
      let createdAt = new Date();
      let operation = Operation.Post;
      let offlineId = data?.offlineId;
      if (!isAddNewImage) {
        const indexedDb = await getIndexedDb();
        const currentItem = await indexedDb.get(id);
        createdAt = currentItem?.data.createdAt;
        offlineId = currentItem?.data?.offlineId;
      }

      if (!offlineId) {
        operation = Operation.Patch;
      }

      await addIndexedDBItem({
        id,
        data: {
          id,
          requestTime: Date.now(),
          status: UpdateToOnlineStatus.Success,
          store: StoreName.PIN_CONTENT_IMAGE,
          isPushed: {},
          operation,
          data: {
            id,
            pinId,
            itemResultId,
            isDefault: false,
            src: data.src,
            originSrc: data.originSrc,
            lastModifiedDateFile: data.lastModifiedDateFile,
            blackboardData: data.blackboardData,
            createdAt,
            offlineId,
          },
        },
      });

      break;
    }

    default:
      break;
  }
};
